import { Typography, Box, Stack, useTheme, Drawer } from "@mui/material";
import Grid from "@mui/material/Grid";
import assetIcon from "../../../assets/onprem_assets.svg";
import securityScoreIcon from "../../../assets/onprem_security_score.svg";
import misconfigIcon from "../../../assets/onprem_misconfig.svg";
import { ComplianceCellRenderer } from "../cell_renderers";
import awsIcon from "../../../assets/dashboard_aws.svg";
import gcpIcon from "../../../assets/dashboard_gcp.svg";
import sparkIcon from "../../../assets/dashboard_azure.svg";
import "./account.scss";
import "../onprem/region/region.scss";
import { Metric } from "../shared";
import { ICloudAccount } from "../shared/types";
import Tooltip from "@mui/material/Tooltip";
import { useState } from "react";
import ResourceCount from "./ResourceCount";
import CloudMisconfiguration from "./Cloud_Misconfiguration";
import CloudSecurityScore from "./Cloud_SecurityScore";
import CloudPercentageCompliance from "./Cloud_Percentage_Compliance";

const getImage = (cloudName: string) => {
  switch (cloudName.toLowerCase()) {
    case "amazon web services":
      return awsIcon;
    case "google cloud platform":
      return gcpIcon;
    case "microsoft azure":
      return sparkIcon;
    default:
      return "";
  }
};

interface AccountProps {
  data: ICloudAccount & Record<string, any>;
  onClick?: (data: ICloudAccount) => void;
  selectedCompliance?: string;
}

const Account = ({
  data,
  onClick,
  selectedCompliance = "CIS_8",
}: AccountProps) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [selectedMetric, setSelectedMetric] = useState<string | null>(null);

  const handleMetricClick = (stat: any) => {
    setSelectedMetric(stat.description);
    setDrawerOpen(true);
  };
  // Get the compliance value based on the selected compliance type
  const getComplianceValue = () => {
    // Access the compliance value directly from the data object
    const rawValue = data[selectedCompliance];

    // Handle different value formats
    let numericValue = 0;
    if (typeof rawValue === "number") {
      numericValue = Math.round(rawValue);
    } else if (typeof rawValue === "string") {
      // Remove any % sign if present and parse
      numericValue = Math.round(parseFloat(rawValue.replace("%", "")));
    }

    // Ensure the value is a valid number between 0 and 100
    numericValue = isNaN(numericValue)
      ? 0
      : Math.min(Math.max(numericValue, 0), 100);
    return numericValue;
  };

  const complianceValue = getComplianceValue();
  const [isPercentDrawrerOpen, setIsPercentDrawerOpen] = useState(false);

  return (
    <Grid
      container
      className={isDarkMode ? "prem-item-dark" : "prem-item-light"}
      alignItems="center"
      key={data.id}
      onClick={() => onClick && onClick(data)}
      style={{ cursor: onClick ? "pointer" : "default" }}
    >
      {/* Horizontal scroll container for tablet screens */}
      <Box
        sx={{
          width: "100%",
          overflowX: { xs: "auto", md: "visible" }, // Horizontal scroll on tablet and below
          overflowY: "hidden",
          "&::-webkit-scrollbar": {
            height: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#555",
          },
        }}
      >
        <Grid
          container
          sx={{
            minWidth: { xs: "800px", md: "auto" }, // Minimum width on tablet to force scroll
            width: "100%",
            alignItems: "center",
          }}
        >
          <Grid item xs={2}>
            <Box
              className={isDarkMode ? "bg-image-cloud-dark" : "bg-image-cloud"}
            />
            <Grid item xs={12} sm={8}>
              <Stack alignItems="center" justifyContent="center">
                <Tooltip title={data.region_name} arrow>
                  <Typography
                    className="region-title"
                    style={{
                      textOverflow: "ellipsis",
                      marginLeft: "20px",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      width: "90px",
                    }}
                  >
                    {data.region_name}
                  </Typography>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>

          <Grid item xs={3}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <img
                src={getImage(data.vendor)}
                alt={`${data.vendor} icon`}
                style={{ marginTop: "10px" }}
              />
              <Stack direction="row" alignItems="center">
                <Typography className="region-title">
                  {data.account_name}
                </Typography>
                <Typography className="accountId">{data.account_id}</Typography>
              </Stack>
            </Stack>
          </Grid>

          {[
            {
              icon: assetIcon,
              value: data.resource_count,
              description: "Resource Count",
            },
            {
              icon: securityScoreIcon,
              value: data.score,
              description: "Security Score",
            },
            {
              icon: misconfigIcon,
              value: data.vulnerabilities,
              description: "Misconfigurations",
            },
          ].map((stat, index) => (
            <Grid item xs={1.5} key={index}>
              <Tooltip
                arrow
                title={`${stat.description}: ${stat.value ?? 0}`}
                slotProps={{
                  popper: {
                    sx: {
                      "& .MuiTooltip-tooltip": {
                        backgroundColor: "#fff",
                        color: "#000",
                        fontSize: "0.75rem",
                        border: "1px solid #ddd",
                        boxShadow: 2,
                        maxWidth: 250, // limit width
                        whiteSpace: "normal", // allow wrapping
                        wordBreak: "break-word",
                      },
                      "& .MuiTooltip-arrow": {
                        color: "#fff",
                      },
                    },
                  },
                }}
              >
                <Box sx={{ cursor: "default", "& > *": { cursor: "default !important" } }}>
                  <Metric
                    index={index}
                    icon={stat.icon}
                    value={stat.value || 0}
                    icon_desc={stat.description || ""}
                    onClick={() => handleMetricClick(stat)}
                  />
                </Box>
              </Tooltip>
            </Grid>
          ))}

          <Drawer
            anchor="right"
            // open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            sx={{
              "& .MuiDrawer-paper": {
                width: { xs: "100%", sm: "50%" },
                bgcolor:
                  theme.palette.mode === "dark" ? "background.paper" : "white",
                boxShadow: "-4px 0 8px rgba(0, 0, 0, 0.1)",
              },
            }}
          >
            <div style={{ padding: 16 }}>
              {selectedMetric === "Resource Count" && (
                <ResourceCount
                  setDrawerOpen={setDrawerOpen}
                  CloudID={data.account_id}
                />
              )}
              {selectedMetric === "Misconfigurations" && (
                <CloudMisconfiguration setDrawerOpen={setDrawerOpen} />
              )}
              {selectedMetric === "Security Score" && (
                <CloudSecurityScore setDrawerOpen={setDrawerOpen} />
              )}
            </div>
          </Drawer>

          {/* Compliance Renderer */}
          <Grid item xs={2} className="prem-item-right">
            <Box className="right-content">
              <Box
                className="stat-item"
                onClick={() => setIsPercentDrawerOpen(false)}
                sx={{ cursor: "default", "& *": { cursor: "default !important" } }}
              >
                <ComplianceCellRenderer value={complianceValue} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Drawer
        anchor="right"
        open={isPercentDrawrerOpen}
        onClose={() => setIsPercentDrawerOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: "50%" },
            bgcolor:
              theme.palette.mode === "dark" ? "background.paper" : "white",
            boxShadow: "-4px 0 8px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <div
          style={{ padding: 16, cursor: "pointer" }}
          // onClick={() => setIsPercentDrawerOpen(false)}
        >
          <CloudPercentageCompliance setDrawerOpen={setIsPercentDrawerOpen} />
        </div>
      </Drawer>
    </Grid>
  );
};

export default Account;
